import React, { useEffect, useId, useState } from "react";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import {
  EMPTY_PROFILE,
  MEDICATION_CLASSES,
  getMedicalProfile,
  saveMedicalProfile,
  toForm,
  pharmacyToProfileFields,
} from "../../utils/medicalProfileApi";
import PharmacySearch from "./PharmacySearch";
import MedicationNotice from "./MedicationNotice";

const cardStyle = { padding: "24px", borderRadius: "8px", marginBottom: "24px", backgroundColor: "var(--card)", border: "1px solid var(--border)" };
const labelStyle = { fontSize: "0.875rem", fontWeight: 500, display: "block", marginBottom: "6px" };
const textStyle = (editing) => ({
  width: "100%",
  minHeight: "64px",
  borderRadius: "6px",
  border: "1px solid var(--youro-border)",
  backgroundColor: editing ? "var(--youro-card)" : "var(--youro-sand)",
  padding: "8px 12px",
  fontSize: "0.875rem",
  color: "var(--youro-charcoal)",
  boxSizing: "border-box",
  resize: "vertical",
  fontFamily: "inherit",
});

const TextArea = ({ label, hint, value, onChange, editing }) => {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <textarea id={id} value={value} readOnly={!editing} maxLength={2000}
                placeholder={editing ? hint : "Not added yet"}
                onChange={(e) => onChange(e.target.value)} style={textStyle(editing)} />
    </div>
  );
};

/**
 * Medical information on the patient's Profile: preferred pharmacy, medications,
 * allergies, medical and surgical history, and whether they take any of the
 * medications their provider needs to know about.
 *
 * The intake form fills these in, and lets a patient skip what they don't have
 * to hand, saying they can add it here later. This is that place.
 *
 * @param Header the profile page's own section header, so the cards match.
 */
const MedicalInfoCard = ({ patientId, Header }) => {
  const [saved, setSaved] = useState(EMPTY_PROFILE);
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [editing, setEditing] = useState(false);
  const [changingPharmacy, setChangingPharmacy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Edit is offered only once what is on file has loaded. Saving replaces every
  // field, so a Save from the empty form — after a failed or slow load — would
  // erase the patient's allergies, pharmacy and history.
  const [loaded, setLoaded] = useState(false);
  const [, guard] = useSubmitGuard();

  useEffect(() => {
    if (!patientId) return;
    getMedicalProfile(patientId)
      .then((p) => {
        setSaved(toForm(p));
        setForm(toForm(p));
        setLoaded(true);
      })
      .catch(() => setLoadFailed(true));
  }, [patientId]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleClass = (id) =>
    setForm((f) => {
      const current = f.reportedMedicationClasses || [];
      const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
      // Unticking the last box is not answering "None of these": go back to
      // what was on file if that was "never answered".
      const unanswered = next.length === 0 && saved.reportedMedicationClasses == null;
      return { ...f, reportedMedicationClasses: unanswered ? null : next };
    });

  const save = guard(async () => {
    try {
      const p = await saveMedicalProfile(patientId, form);
      setSaved(toForm(p));
      setForm(toForm(p));
      setEditing(false);
      setChangingPharmacy(false);
      toast.success("Your medical information is saved. Your provider will see it before your next visit.");
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't save your medical information. Check it and try again."));
    }
  });

  const cancel = () => {
    setForm(saved);
    setEditing(false);
    setChangingPharmacy(false);
  };

  const reported = form.reportedMedicationClasses;
  const answeredNone = Array.isArray(reported) && reported.length === 0;
  const hasPharmacy = Boolean(form.pharmacyName);

  return (
    <div style={cardStyle}>
      {loaded ? (
        <Header title="Medical Information" isEditing={editing} onEdit={() => setEditing(true)} onSave={save} />
      ) : (
        <h3 style={{ margin: "0 0 24px", fontSize: "1.25rem", fontWeight: 600 }}>Medical Information</h3>
      )}
      {!loaded && !loadFailed && (
        <p style={{ marginTop: 0, fontSize: "0.875rem", color: "var(--youro-muted)" }}>Loading…</p>
      )}

      {loadFailed && (
        <p role="alert" style={{ color: "var(--youro-error)", fontSize: "0.875rem", marginTop: 0 }}>
          We couldn't load your medical information. Refresh the page to try again.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Preferred pharmacy */}
        <div>
          <span style={labelStyle}>Preferred pharmacy</span>
          {hasPharmacy ? (
            <div style={{ padding: "10px 12px", borderRadius: "6px", backgroundColor: "var(--youro-sand)", border: "1px solid var(--youro-border)" }}>
              <span style={{ display: "block", fontWeight: 600, fontSize: "0.875rem" }}>{form.pharmacyName}</span>
              {form.pharmacyAddress && <span style={{ display: "block", fontSize: "0.8125rem", color: "var(--youro-muted)" }}>{form.pharmacyAddress}</span>}
              {form.pharmacyPhone && <span style={{ display: "block", fontSize: "0.8125rem", color: "var(--youro-muted)" }}>Phone {form.pharmacyPhone}</span>}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--youro-muted)" }}>Not added yet</p>
          )}
          {editing && !changingPharmacy && (
            <button type="button" onClick={() => setChangingPharmacy(true)}
                    style={{ marginTop: "8px", padding: "6px 0", border: "none", background: "transparent", color: "var(--youro-sky)", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer" }}>
              {hasPharmacy ? "Change pharmacy" : "Find my pharmacy"}
            </button>
          )}
          {editing && changingPharmacy && (
            <div style={{ marginTop: "12px" }}>
              <PharmacySearch onSelect={(p) => {
                setForm((f) => ({ ...f, ...pharmacyToProfileFields(p) }));
                setChangingPharmacy(false);
              }} />
              <button type="button" onClick={() => setChangingPharmacy(false)}
                      style={{ marginTop: "8px", padding: "6px 0", border: "none", background: "transparent", color: "var(--youro-charcoal)", fontSize: "0.875rem", cursor: "pointer" }}>
                Keep my current pharmacy
              </button>
            </div>
          )}
        </div>

        <TextArea label="Medications" hint="Every medication you take, separated by commas, including supplements"
                  value={form.medications} onChange={set("medications")} editing={editing} />
        {/* As on the intake form: shown while the patient edits their medications. */}
        {editing && <MedicationNotice />}

        {/* The four the provider must know about, asked directly as on the intake form. */}
        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={labelStyle}>Do you take any of these medications?</legend>
          {!editing && reported == null && (
            <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--youro-muted)" }}>Not answered yet</p>
          )}
          {!editing && answeredNone && (
            <p style={{ margin: 0, fontSize: "0.875rem" }}>None of these</p>
          )}
          {(editing || (reported && reported.length > 0)) && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {MEDICATION_CLASSES.filter((c) => editing || (reported || []).includes(c.id)).map((c) => (
                <label key={c.id} style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "0.875rem", cursor: editing ? "pointer" : "default" }}>
                  <input type="checkbox" disabled={!editing} checked={(reported || []).includes(c.id)}
                         onChange={() => toggleClass(c.id)}
                         style={{ width: "16px", height: "16px", marginTop: "2px", accentColor: "var(--youro-sage)" }} />
                  <span><strong>{c.label}</strong> <span style={{ color: "var(--youro-muted)" }}>({c.examples})</span></span>
                </label>
              ))}
              {editing && (
                <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "0.875rem", cursor: "pointer" }}>
                  <input type="checkbox" checked={answeredNone}
                         onChange={() => setForm((f) => ({ ...f, reportedMedicationClasses: answeredNone ? null : [] }))}
                         style={{ width: "16px", height: "16px", marginTop: "2px", accentColor: "var(--youro-sage)" }} />
                  <span>None of these</span>
                </label>
              )}
            </div>
          )}
        </fieldset>

        <TextArea label="Allergies" hint="Medication allergies and the reaction each causes"
                  value={form.allergies} onChange={set("allergies")} editing={editing} />
        <TextArea label="Medical history" hint="Conditions you have or have had, e.g. diabetes, high blood pressure"
                  value={form.medicalHistory} onChange={set("medicalHistory")} editing={editing} />
        <TextArea label="Surgical history" hint="Surgeries you have had, with the year if you know it"
                  value={form.surgicalHistory} onChange={set("surgicalHistory")} editing={editing} />

        {editing && (
          <button type="button" onClick={cancel}
                  style={{ alignSelf: "flex-start", padding: "8px 0", border: "none", background: "transparent", color: "var(--youro-charcoal)", fontSize: "0.875rem", cursor: "pointer" }}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default MedicalInfoCard;
